import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import NewChatLayout from './NewChatLayout';
import { fetchCHatList } from '../../store/actions/DataAction';
import { getChatService, ChatMessage, ChatAttachment } from '../../services/PubSubChatService';
import { v4 as uuidv4 } from 'uuid';
import { tokenProvider } from '../../utils/apis';
import { useLicenseGate } from '../../hooks/useLicenseGate';

type HistoryRow = {
  MessageID: string;
  ChatID: string;
  SenderID: string;
  Content: string;
  MessageType: string;
  CreatedAt: string;
  UpdatedAt?: string;
  Attachments?: any[];
};

const PAGE_SIZE = 50;

const NewMessageCenter: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { checkSectionGate } = useLicenseGate();

  const chatList = useSelector((state: RootState) => state.lists.chatList);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  const myUserId = userInfo?.UserID ? String(userInfo.UserID) : undefined;
  const myUserUID = userInfo?.UserUID || userInfo?.uuid_encrypted || undefined;

  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const selectedChat: any = chatList.find((chat: any) => String(chat.ChatID) === String(selectedChatId));

  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [newChat, setNewChat] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isChatLoading, setIsChatLoading] = useState(false);

  const [unreadCounts, setUnreadCounts] = useState<any>({});
  const [messages, setMessages] = useState<any[]>([]);
  const [userMap, setUserMap] = useState<Record<string, any>>({});

  // pagination state (offset based)
  const [page, setPage] = useState(1); // 1-based, page=1 => offset=0
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  const [uploadingImages, setUploadingImages] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const [showGallery, setShowGallery] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [myStream, setMyStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [callAccepted, setCallAccepted] = useState(false);
  const [mute, setMute] = useState(false);
  const [incomingCall, setIncomingCall] = useState(false);
  const [callId, setCallId] = useState('');
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isRemoteVideoEnabled, setIsRemoteVideoEnabled] = useState(true);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState<any[]>([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [activeChatTab, setActiveChatTab] = useState('chats');
  const [replyToId, setReplyToId] = useState<string | null>(null);

  const chatService = useRef(getChatService()).current;

  const unsubscribeMessageRef = useRef<(() => void) | undefined>(undefined);
  const unsubscribeTypingRef = useRef<(() => void) | undefined>(undefined);
  const unsubscribeSentAckRef = useRef<(() => void) | undefined>(undefined);
  const unsubscribeDeliveredRef = useRef<(() => void) | undefined>(undefined);

  const typingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // [OK] de-dupe across history + realtime
  const serverIdSetRef = useRef<Set<string>>(new Set());

  // [OK] avoid mixing responses between chat switches
  const activeChatIdRef = useRef<string | null>(null);

  // [OK] Read receipts control
  const readSentByChatRef = useRef<Record<string, Set<string>>>({});
  const suppressReadForHistoryRef = useRef(false);

  const getReadSet = (chatId: string) => {
    const key = String(chatId);
    if (!readSentByChatRef.current[key]) readSentByChatRef.current[key] = new Set();
    return readSentByChatRef.current[key];
  };

  // IMPORTANT: if your server ids are UUID, replace this with UUID test.
  const isServerMessageId = (id: string) => {
    return /^\d+$/.test(id);
  };

  // ----------------------------
  // History helpers
  // ----------------------------

  const normalizeHistoryRow = useCallback(
    (row: HistoryRow) => {
      const serverId = String(row.MessageID);
      const senderId = String(row.SenderID || '');
      const msgType = (row.MessageType || 'text').toLowerCase();

      const content = row.Content;

      const attachments: ChatAttachment[] = Array.isArray((row as any).Attachments)
        ? (row as any).Attachments
        : [];

      const base = {
        id: serverId,
        MessageID: serverId,
        sender: senderId,
        SenderID: senderId,
        sender_id: senderId,
        content,
        attachments,
        sentAt: row.CreatedAt,
        timestamp: row.CreatedAt,
        message_type: msgType === 'file' ? 'file' : 'text',
      };

      // history messages are already delivered; show "delivered" for my messages only
      if (senderId === String(myUserId)) {
        return { ...base, status: 'delivered' as const };
      }

      return base;
    },
    [myUserId]
  );

  const mergeHistory = useCallback((historyMessages: any[], mode: 'replace' | 'prepend') => {
    setMessages(prev => {
      if (mode === 'replace') return historyMessages;
      return [...historyMessages, ...prev];
    });
  }, []);

  // ----------------------------
  // History API - Streaming NDJSON
  // ----------------------------

  const streamMessagesFromAPI = useCallback(
    async (chatId: string, limit: number = PAGE_SIZE, offset: number = 0): Promise<HistoryRow[]> => {
      // Get token from tokenProvider
      let token: string | null = null;
      try {
        if (tokenProvider) {
          token = await Promise.resolve(tokenProvider());
        }
      } catch (e) {
        console.warn('[NewMessageCenter] Failed to get token for streaming:', e);
      }

      if (!token) {
        throw new Error(t('No authentication token available for message streaming'));
      }

      try {
        // Use encrypted streaming endpoint with client-side decryption
        const decryptedMessages: any = []


        // Convert decrypted messages to HistoryRow format for compatibility
        const historyRows: HistoryRow[] = decryptedMessages.map((msg: any) => ({
          MessageID: String(msg.id || ''),
          ChatID: String(msg.chat_id || ''),
          SenderID: String(msg.sender_id || ''),
          Content: msg.content || '',
          MessageType: msg.message_type || 'text',
          CreatedAt: msg.created_at || '',
          UpdatedAt: msg.updated_at,
          Attachments: msg.attachments || [],
        }));

        return historyRows;
      } catch (error) {
        console.error('[NewMessageCenter] Failed to stream encrypted messages:', error);
        throw error;
      }
    },
    []
  );

  const fetchMessagesPage = useCallback(
    async (chatId: string, pageNumber: number) => {
      const offset = (pageNumber - 1) * PAGE_SIZE;

      try {
        const messages = await streamMessagesFromAPI(chatId, PAGE_SIZE, offset);

        // Calculate pagination info
        // Since we don't have total count from stream, we check if we got fewer messages than requested
        const has_more = messages.length >= PAGE_SIZE;

        return {
          success: true,
          messages,
          total: messages.length,
          has_more,
        };
      } catch (error) {
        console.error('[NewMessageCenter] fetchMessagesPage error:', error);
        return {
          success: false,
          messages: [],
          total: 0,
          has_more: false,
        };
      }
    },
    [streamMessagesFromAPI]
  );

  const loadInitialMessages = useCallback(
    async (chatId: string) => {
      if (!chatId) return;

      setIsFetching(true);
      try {
        // reset dedupe for new chat
        serverIdSetRef.current = new Set();
        // reset read set for new chat
        readSentByChatRef.current[String(chatId)] = new Set();

        const data = await fetchMessagesPage(chatId, 1);
        if (!data?.success) {
          setMessages([]);
          setHasMore(false);
          setPage(1);
          return;
        }

        // DESC from API -> ASC for UI
        const normalizedAsc = (data.messages || []).map(normalizeHistoryRow).reverse();

        normalizedAsc.forEach(m => {
          const id = String(m.id || m.MessageID || '');
          if (id) serverIdSetRef.current.add(id);
        });

        mergeHistory(normalizedAsc, 'replace');
        setPage(1);
        setHasMore(Boolean(data.has_more));

        // [OK] Mark read only for the newest few messages (NOT older history)
        try {
          const readSet = getReadSet(chatId);
          const toRead: string[] = [];

          const tail = normalizedAsc.slice(-10);
          for (const m of tail) {
            const sender = String(m.sender || m.SenderID || m.sender_id || '');
            const id = String(m.id || m.MessageID || '');
            if (!id || !isServerMessageId(id)) continue;
            if (sender === String(myUserId)) continue;
            if (readSet.has(id)) continue;
            readSet.add(id);
            toRead.push(id);
          }

          // fire read receipts once (per id) for latest only
          if (toRead.length) {
            toRead.forEach(mid => chatService.markMessageAsRead(chatId, mid).catch(() => {}));
          }
        } catch {}
      } catch (e) {
        console.error('[NewMessageCenter] loadInitialMessages error:', e);
        setMessages([]);
        setHasMore(false);
        setPage(1);
      } finally {
        setIsFetching(false);
      }
    },
    [fetchMessagesPage, normalizeHistoryRow, mergeHistory, myUserId, chatService]
  );

  const loadMoreMessages = useCallback(async () => {
    if (!selectedChatId) return;
    if (isFetching) return;
    if (!hasMore) return;

    suppressReadForHistoryRef.current = true;
    setIsFetching(true);

    try {
      const nextPage = page + 1;
      const data = await fetchMessagesPage(selectedChatId, nextPage);

      if (!data?.success) {
        setHasMore(false);
        return;
      }

      const normalizedAsc = (data.messages || []).map(normalizeHistoryRow).reverse();

      const unique = normalizedAsc.filter(m => {
        const id = String(m.id || m.MessageID);
        if (!id) return false;
        if (serverIdSetRef.current.has(id)) return false;
        serverIdSetRef.current.add(id);
        return true;
      });

      if (unique.length > 0) mergeHistory(unique, 'prepend');

      setPage(nextPage);
      setHasMore(Boolean(data.has_more));
    } catch (e) {
      console.error('[NewMessageCenter] loadMoreMessages error:', e);
    } finally {
      setIsFetching(false);
      suppressReadForHistoryRef.current = false;
    }
  }, [selectedChatId, isFetching, hasMore, page, fetchMessagesPage, normalizeHistoryRow, mergeHistory]);

  // ----------------------------
  // Initialize pubsub + chat list
  // ----------------------------

  useEffect(() => {
    if (!myUserId) {
      return;
    }

    const init = async () => {
      try {
        const ok = await chatService.initialize(myUserId);
        if (!ok) console.error('[NewMessageCenter] Failed to initialize chat service');
      } catch (e) {
        console.error('[NewMessageCenter] init error:', e);
      } finally {
        setIsLoading(false);
      }
    };

    init();
    dispatch(fetchCHatList() as any);

    return () => {
      unsubscribeMessageRef.current?.();
      unsubscribeTypingRef.current?.();
      unsubscribeSentAckRef.current?.();
      unsubscribeDeliveredRef.current?.();
    };
  }, [myUserId, dispatch, chatService]);

  // ----------------------------
  // Build user map
  // ----------------------------

  useEffect(() => {
    if (!userInfo?.UserID) return;

    const newMap: Record<string, any> = {};
    const setUser = (key: any, val: any) => {
      if (key !== undefined && key !== null && String(key).trim() !== '') {
        newMap[String(key)] = val;
      }
    };

    const me = {
      UserID: String(userInfo.UserID),
      UserUID: myUserUID,
      Name: `${userInfo.FirstName} ${userInfo.LastName}`.trim(),
      Email: userInfo.Email,
      ProfilePicture: userInfo.ProfilePicture,
      Initials: `${userInfo.FirstName?.[0] || ''}${userInfo.LastName?.[0] || ''}`,
      Role: userInfo.Role,
      FirstName: userInfo.FirstName,
      LastName: userInfo.LastName,
    };

    setUser(me.UserID, me);
    setUser(me.UserUID, me);

    chatList.forEach((chat: any) => {
      if (!chat.isGroup && chat.userData) {
        const other = {
          UserID: chat.userData.UserID ? String(chat.userData.UserID) : undefined,
          UserUID: chat.userData.UserUID,
          Name: `${chat.userData.FirstName} ${chat.userData.LastName}`.trim(),
          Email: chat.userData.Email,
          ProfilePicture: chat.userData.ProfilePicture,
          Initials: chat.userData.Initials || `${chat.userData.FirstName?.[0] || ''}${chat.userData.LastName?.[0] || ''}`,
          Role: chat.userData.Role,
          FirstName: chat.userData.FirstName,
          LastName: chat.userData.LastName,
        };
        setUser(other.UserID, other);
        setUser(other.UserUID, other);
      }

      if (chat.isGroup && chat.GroupData?.Members?.length) {
        chat.GroupData.Members.forEach((m: any) => {
          const u = {
            UserID: m.UserID ? String(m.UserID) : undefined,
            UserUID: m.UserUID,
            Name: `${m.FirstName} ${m.LastName}`.trim(),
            Email: m.Email,
            ProfilePicture: m.ProfilePicture,
            Initials: m.Initials || `${m.FirstName?.[0] || ''}${m.LastName?.[0] || ''}`,
            Role: m.Role,
            FirstName: m.FirstName,
            LastName: m.LastName,
          };
          setUser(u.UserID, u);
          setUser(u.UserUID, u);
        });
      }
    });

    setUserMap(newMap);
  }, [userInfo, myUserUID, chatList]);

  // ----------------------------
  // Chat selection: history then realtime
  // ----------------------------

  useEffect(() => {
    if (!selectedChatId || !myUserId) return;

    let isCancelled = false;

    const run = async () => {
      activeChatIdRef.current = selectedChatId;

      setIsChatLoading(true);

      // cleanup old subscriptions
      unsubscribeMessageRef.current?.();
      unsubscribeTypingRef.current?.();
      unsubscribeSentAckRef.current?.();
      unsubscribeDeliveredRef.current?.();

      // 1) load initial history
      await loadInitialMessages(selectedChatId);

      if (isCancelled) return;
      if (activeChatIdRef.current !== selectedChatId) return;

      // 2) join realtime
      try {
        await chatService.joinChat(selectedChatId);
      } catch (e) {
        console.error('[NewMessageCenter] joinChat error:', e);
      }

      // 3) ack (single tick)
      unsubscribeSentAckRef.current = chatService.onMessageSent(selectedChatId, (ack: any) => {
        if (isCancelled) return;
        if (activeChatIdRef.current !== selectedChatId) return;

        const localId = String(ack.local_message_id || '');
        const serverId = String(ack.message_id || '');

        if (!localId) return;

        if (ack.success === false || ack.success === 'False') {
          setMessages(prev =>
            prev.map(m =>
              String(m.local_message_id || m.id || m.MessageID) === localId
                ? { ...m, status: 'failed', error: ack.error || 'Failed' }
                : m
            )
          );
          return;
        }

        if (!serverId) return;

        serverIdSetRef.current.add(serverId);

        setMessages(prev => {
          const idx = prev.findIndex(m => String(m.local_message_id || m.id || m.MessageID) === localId);
          if (idx === -1) return prev;

          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            id: serverId,
            MessageID: serverId,
            status: 'sent',
            sentAt: ack.timestamp || updated[idx].sentAt || new Date().toISOString(),
            timestamp: ack.timestamp || updated[idx].timestamp || new Date().toISOString(),
          };
          return updated;
        });
      });

      // 4) delivered (double tick)
      unsubscribeDeliveredRef.current = chatService.onMessageDelivered(selectedChatId, (data: any) => {
        if (isCancelled) return;
        if (activeChatIdRef.current !== selectedChatId) return;

        const deliveredId = String(data.message_id || '');
        if (!deliveredId) return;

        setMessages(prev =>
          prev.map(m => {
            const id = String(m.id || m.MessageID);
            if (id !== deliveredId) return m;
            if (m.status === 'delivered') return m;
            return { ...m, status: 'delivered', deliveredAt: data.timestamp || new Date().toISOString() };
          })
        );
      });

      // 5) incoming message
      unsubscribeMessageRef.current = chatService.onMessage(selectedChatId, (msg: ChatMessage) => {
        if (isCancelled) return;
        if (activeChatIdRef.current !== selectedChatId) return;

        const serverId = String(msg.id || '');
        const senderId = String(msg.sender_id || '');

        if (!serverId) return;

        // dedupe
        if (serverIdSetRef.current.has(serverId)) return;
        serverIdSetRef.current.add(serverId);

        // ignore echo of my own
        if (senderId === String(myUserId)) return;

        // append
        setMessages(prev => [
          ...prev,
          {
            id: serverId,
            MessageID: serverId,
            sender: senderId,
            SenderID: senderId,
            sender_id: senderId,
            content: msg.content,
            attachments: msg.attachments || [],
            sentAt: msg.timestamp,
            timestamp: msg.timestamp,
            message_type: msg.message_type,
          },
        ]);

        // [OK] Mark read ONLY for realtime new messages (and only once)
        if (!suppressReadForHistoryRef.current && isServerMessageId(serverId)) {
          const readSet = getReadSet(selectedChatId);
          if (!readSet.has(serverId)) {
            readSet.add(serverId);
            chatService.markMessageAsRead(selectedChatId, serverId).catch(() => {});
          }
        }
      });

      // 6) typing
      unsubscribeTypingRef.current = chatService.onTyping(selectedChatId, (data: any) => {
        if (isCancelled) return;
        if (activeChatIdRef.current !== selectedChatId) return;

        const typingUserId = String(data.user_id || data.userId || '');
        if (!typingUserId || typingUserId === String(myUserId)) return;

        if (data.stopped) {
          setIsTyping(false);
          return;
        }

        setIsTyping(true);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 2000);
      });

      setIsChatLoading(false);
    };

    run().catch(e => {
      console.error('[NewMessageCenter] chat run error:', e);
      setIsChatLoading(false);
    });

    return () => {
      isCancelled = true;
      unsubscribeMessageRef.current?.();
      unsubscribeTypingRef.current?.();
      unsubscribeSentAckRef.current?.();
      unsubscribeDeliveredRef.current?.();
      chatService.leaveChat(selectedChatId).catch(() => {});
    };
  }, [selectedChatId, myUserId, chatService, loadInitialMessages]);

  // ----------------------------
  // Send message (FIXED for attachments)
  // ----------------------------

  const handleSendMessage = useCallback(
    async (messageText: string, attachments: ChatAttachment[] = []) => {
      if (!checkSectionGate('messages')) return;
      if (!messageText.trim() && !attachments.length) return;
      if (!selectedChatId || !myUserId) return;

      // ensure joined
      if (!chatService.getActiveChats().includes(selectedChatId)) {
        const joined = await chatService.joinChat(selectedChatId);
        if (!joined) return;
      }

      const localMessageId = uuidv4();
      const msgType = attachments?.length ? 'file' : 'text';

      const optimistic = {
        id: localMessageId,
        MessageID: localMessageId,
        sender: myUserId,
        SenderID: myUserId,
        sender_id: myUserId,
        content: messageText,
        attachments: attachments || [],
        sentAt: new Date().toISOString(),
        timestamp: new Date().toISOString(),
        message_type: msgType,
        local_message_id: localMessageId,
        status: 'sending' as const,
      };

      setMessages(prev => [...prev, optimistic]);

      // [OK] IMPORTANT: PubSubChatService must accept attachments param.
      const sentLocalId = await chatService.sendMessage(
        selectedChatId,
        messageText,
        msgType as any,
        localMessageId,
        attachments
      );

      if (!sentLocalId) {
        setMessages(prev => prev.map(m => (m.id === localMessageId ? { ...m, status: 'failed' } : m)));
      }
      // Do NOT set "sent" here — ACK will set it.
    },
    [selectedChatId, myUserId, chatService]
  );

  const handleTyping = useCallback(() => {
    if (!selectedChatId) return;

    chatService.sendTypingIndicator(selectedChatId).catch(() => {});
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    typingTimeoutRef.current = setTimeout(() => {
      chatService.sendStopTypingIndicator(selectedChatId).catch(() => {});
    }, 2000);
  }, [selectedChatId, chatService]);

  // [OK] Keep this handler but it will NOT spam due to Set guard.
  // You can use it if later you implement viewport-based read.
  const handleMessagesRead = useCallback(
    (messageIds: string[]) => {
      if (!selectedChatId) return;

      // prevent history-triggered spam
      if (suppressReadForHistoryRef.current) return;

      const readSet = getReadSet(selectedChatId);

      for (const mid of messageIds) {
        const id = String(mid || '');
        if (!id || !isServerMessageId(id)) continue;
        if (readSet.has(id)) continue;
        readSet.add(id);
        chatService.markMessageAsRead(selectedChatId, id).catch(() => {});
      }
    },
    [selectedChatId, chatService]
  );

  const handleSelectChat = useCallback((chatId: string, chat: any) => {
    setPage(1);
    setHasMore(true);
    setSelectedChatId(String(chatId));
    setSelectedEmployee(chat.userData || null);
    setNewChat(false);
  }, []);

  const handleStartCall = useCallback((shouldStart: boolean) => {
    if (shouldStart) setCallActive(true);
    else {
      setCallActive(false);
      setCallAccepted(false);
      setIncomingCall(false);
    }
  }, []);

  const handleReply = useCallback((messageId: string) => setReplyToId(messageId), []);
  const handleClearReply = useCallback(() => setReplyToId(null), []);

  return (
    <NewChatLayout
      selectedChatId={selectedChatId}
      selectedChat={selectedChat}
      selectedEmployee={selectedEmployee}
      newChat={newChat}
      isLoading={isLoading}
      isChatLoading={isChatLoading}
      messages={messages}
      userMap={userMap}
      chatList={chatList}
      unreadCounts={unreadCounts}
      uploadingImages={uploadingImages}
      isTyping={isTyping}
      showGallery={showGallery}
      callActive={callActive}
      myStream={myStream}
      remoteStream={remoteStream}
      callAccepted={callAccepted}
      mute={mute}
      incomingCall={incomingCall}
      callId={callId}
      isVideoEnabled={isVideoEnabled}
      isRemoteVideoEnabled={isRemoteVideoEnabled}
      showNotes={showNotes}
      notes={notes}
      isMinimized={isMinimized}
      isMaximized={isMaximized}
      activeChatTab={activeChatTab}
      page={page}
      hasMore={hasMore}
      isFetching={isFetching}
      replyToId={replyToId}
      onSelectChat={handleSelectChat}
      onSendMessage={handleSendMessage}
      onTyping={handleTyping}
      onMessagesRead={handleMessagesRead}
      onReply={handleReply}
      onClearReply={handleClearReply}
      onStartCall={handleStartCall}
      loadMoreMessages={loadMoreMessages} // [OK] real paging function
      setCallActive={setCallActive}
      setMute={setMute}
      setIncomingCall={setIncomingCall}
      setCallId={setCallId}
      setIsVideoEnabled={setIsVideoEnabled}
      setIsRemoteVideoEnabled={setIsRemoteVideoEnabled}
      setShowNotes={setShowNotes}
      setNotes={setNotes}
      setIsMinimized={setIsMinimized}
      setIsMaximized={setIsMaximized}
      setActiveChatTab={setActiveChatTab}
      setPage={setPage}
      setShowGallery={setShowGallery}
      userUID={myUserUID}
      currentUID={myUserUID}
    />
  );
};

export default NewMessageCenter;
